<template>
    <div v-if="show" class="viewer-modal" :style="modalStyle">
        <!-- Header (Drag Handle) -->
        <div class="viewer-header" @mousedown.prevent="startDrag">
            <span class="viewer-title">
                Source Document - {{ currentFile.file_name }} ({{ currentIndex + 1 }}/{{ sourceFiles.length }})
            </span>
            <div class="viewer-header-buttons">
                <button @click.stop="prevFile" :disabled="currentIndex === 0">⬅</button>
                <button @click.stop="nextFile" :disabled="currentIndex === sourceFiles.length - 1">➡</button>
                <button class="viewer-close" @click="$emit('close')">✕</button>
            </div>
        </div>

        <!-- Content -->
        <div class="viewer-body">
            <div v-if="loading" class="viewer-loading">Loading document...</div>

            <!-- PDF -->
            <pdf-app v-if="fileType === 'pdf' && fileUrl" :pdf="fileUrl" class="pdf-viewer" />

            <!-- TIFF -->
            <div v-if="fileType === 'tiff'" class="tiff-viewer" :style="{ transform: `scale(${scale})` }">
                <canvas v-for="(canvas, index) in tiffCanvases" :key="index" :ref="el => registerCanvas(el, index)" />
            </div>

            <!-- IMAGE -->
            <div v-if="fileType === 'image' && fileUrl" class="image-viewer">
                <img :src="fileUrl" alt="Source document" :style="{ transform: `scale(${scale})` }" />
            </div>

            <div v-if="error" class="viewer-error">{{ error }}</div>
        </div>

        <!-- Resize Handle -->
        <div class="resize-handle" @mousedown.prevent="startResize" />
    </div>
</template>

<script>
import Tiff from 'tiff'
import VuePdfApp from 'vue3-pdf-app'
import 'vue3-pdf-app/dist/icons/main.css'
import { getBinary, get } from '../../utils/api.js'

export default {
    name: 'ViewSourceDocument',

    components: {
        'pdf-app': VuePdfApp,
    },

    props: {
        sourceFiles: { type: Array, required: true },
        show: { type: Boolean, required: true },
    },

    data() {
        return {
            loading: false,
            error: null,
            fileUrl: null,
            fileType: null,
            scale: 1,
            tiffCanvases: [],

            // modal state
            x: 100,
            y: 80,
            width: 800,
            height: 600,

            dragging: false,
            resizing: false,
            startX: 0,
            startY: 0,
            startWidth: 0,
            startHeight: 0,

            currentIndex: 0,
        }
    },

    computed: {
        modalStyle() {
            return {
                left: this.x + 'px',
                top: this.y + 'px',
                width: this.width + 'px',
                height: this.height + 'px',
            }
        },
        currentFile() {
            return this.sourceFiles[this.currentIndex] || {}
        },
    },

    watch: {
        sourceFiles: {
            immediate: true,
            handler() {
                if (this.sourceFiles.length > 0) {
                    this.currentIndex = 0
                    this.loadFile()
                }
            },
        },
        currentIndex(newVal) {
            if (this.sourceFiles.length > 0) this.loadFile()
        },
    },


    methods: {
        async loadFile() {
            console.log("sourceFiles==>", this.sourceFiles);

            const fileItem = this.currentFile
            if (!fileItem) return

            this.loading = true
            this.error = null
            this.fileUrl = null
            this.tiffCanvases = []
            // 1️⃣ Call backend to get presigned URL
            try {
                const metaResponse = await get(
                    '/v1/s3/get-url',
                    { s3_path: fileItem.s3_path }
                    // { s3_url: fileItem.path }
                )
                console.log("metaResponse==>", metaResponse);

                const presignedUrl = metaResponse?.data?.data?.presigned_url
                if (!presignedUrl) {
                    throw new Error('Download URL not received')
                }
                // 2️⃣ Download file directly from S3
                const response = await this.getBinaryFromUrl(presignedUrl)
                const blob = response.data
                const fileName = fileItem.file_name
                const mimeType =
                    response.headers?.['content-type'] ||
                    this.getMimeFromExtension(fileName)

                const file = new File([blob], fileName, { type: mimeType })

                if (mimeType.includes('pdf')) {
                    this.fileType = 'pdf'
                    this.fileUrl = URL.createObjectURL(file)
                } else if (mimeType.includes('tiff') || mimeType.includes('tif')) {
                    this.fileType = 'tiff'
                    this.renderTiff(blob)

                } else if (mimeType.startsWith('image/')) {
                    this.fileType = 'image'
                    this.fileUrl = URL.createObjectURL(file)

                } else {
                    throw new Error('Unsupported file type')
                }

            } catch (e) {
                this.error = e.message || 'Unable to load document'
            } finally {
                this.loading = false
            }
        },
        async getBinaryFromUrl(url) {
            const res = await fetch(url, {
                method: 'GET',
                credentials: 'omit' // important for S3
            })

            if (!res.ok) {
                throw new Error(`Failed to download file (${res.status})`)
            }

            const blob = await res.blob()

            return {
                data: blob,
                headers: {
                    'content-type': res.headers.get('content-type')
                }
            }
        },

        getMimeFromExtension(fileName) {
            const ext = fileName.split('.').pop().toLowerCase()
            return {
                pdf: 'application/pdf',
                tif: 'image/tiff',
                tiff: 'image/tiff',
                jpg: 'image/jpeg',
                jpeg: 'image/jpeg',
                png: 'image/png',
            }[ext]
        },

        renderTiff(blob) {
            const reader = new FileReader()
            reader.onload = () => {
                const tiff = new Tiff({ buffer: reader.result })
                const pages = tiff.countDirectory()
                for (let i = 0; i < pages; i++) {
                    tiff.setDirectory(i)
                    this.tiffCanvases.push(tiff.toCanvas())
                }
            }
            reader.readAsArrayBuffer(blob)
        },

        registerCanvas(el, index) {
            if (el && this.tiffCanvases[index]) {
                el.replaceWith(this.tiffCanvases[index])
            }
        },

        /* ---------- Navigation ---------- */
        nextFile() {
            if (this.currentIndex < this.sourceFiles.length - 1) this.currentIndex++
        },
        prevFile() {
            if (this.currentIndex > 0) this.currentIndex--
        },

        /* ---------- Drag ---------- */
        startDrag(e) {
            this.dragging = true
            this.startX = e.clientX - this.x
            this.startY = e.clientY - this.y
            document.addEventListener('mousemove', this.onDrag)
            document.addEventListener('mouseup', this.stopDrag)
        },
        onDrag(e) {
            if (!this.dragging) return
            const newX = e.clientX - this.startX
            const newY = e.clientY - this.startY
            // Keep modal within viewport bounds
            this.x = Math.max(0, Math.min(newX, window.innerWidth - this.width))
            this.y = Math.max(0, Math.min(newY, window.innerHeight - this.height))
        },
        stopDrag() {
            this.dragging = false
            document.removeEventListener('mousemove', this.onDrag)
            document.removeEventListener('mouseup', this.stopDrag)
        },

        /* ---------- Resize ---------- */
        startResize(e) {
            this.resizing = true
            this.startX = e.clientX
            this.startY = e.clientY
            this.startWidth = this.width
            this.startHeight = this.height
            document.addEventListener('mousemove', this.onResize)
            document.addEventListener('mouseup', this.stopResize)
        },
        onResize(e) {
            if (!this.resizing) return
            this.width = Math.max(400, this.startWidth + (e.clientX - this.startX))
            // Constrain height so it doesn't go below viewport
            const maxHeight = window.innerHeight - this.y
            this.height = Math.max(300, Math.min(this.startHeight + (e.clientY - this.startY), maxHeight))
        },
        stopResize() {
            this.resizing = false
            document.removeEventListener('mousemove', this.onResize)
            document.removeEventListener('mouseup', this.stopResize)
        },
    },

    beforeUnmount() {
        if (this.fileUrl) URL.revokeObjectURL(this.fileUrl)
    },
}
</script>

<style scoped>
.viewer-modal {
    position: fixed;
    background: #fff;
    border: 1px solid #ddd;
    border-radius: 8px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.25);
    display: flex;
    flex-direction: column;
    overflow: hidden;
    z-index: 2147483647;
}

.viewer-header {
    height: 40px;
    background: #f1f3f5;
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 0 10px;
    cursor: move;
    user-select: none;
}

.viewer-header-buttons {
    display: flex;
    align-items: center;
    gap: 6px;
}

.viewer-header-buttons button {
    display: flex;
    align-items: center;
    gap: 6px;
    background: var(--primary-color);
    /* Use your primary color */
    border: none;
    color: white;
    padding: 6px 12px;
    border-radius: 4px;
    font-family: Archivo, sans-serif;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    transition: all 0.2s;
    white-space: nowrap;
}

.viewer-header-buttons button:hover:not(:disabled) {
    background: #0f3a4f;
    /* Hover color */
    transform: translateY(-1px);
}

.viewer-header-buttons button:disabled {
    background: #a0a0a0;
    /* Disabled color */
    cursor: not-allowed;
    transform: none;
}


.viewer-title {
    font-size: 14px;
    font-weight: 500;
}

.viewer-close {
    background: none;
    border: none;
    font-size: 18px;
    cursor: pointer;
}

.viewer-body {
    flex: 1;
    overflow: auto;
}

.pdf-viewer {
    width: 100%;
    height: 100%;
}

.tiff-viewer {
    padding: 16px;
}

.resize-handle {
    position: absolute;
    width: 20px;
    height: 20px;
    right: 2px;
    bottom: 2px;
    cursor: se-resize;
    background: linear-gradient(135deg, transparent 50%, #999 50%);
    z-index: 2147483648;
    border-radius: 2px;
    opacity: 0.5;
    transition: opacity 0.2s;
}

.resize-handle:hover {
    opacity: 1;
    background: linear-gradient(135deg, transparent 50%, #0f3a4f 50%);
}

.image-viewer {
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 16px;
}

.image-viewer img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
}

.viewer-loading {
    display: flex;
    align-items: center;
    justify-content: center;
}
</style>
